import React from 'react';
import {
    GivelifySwitch,
    GivelifyLabel,
    GivelifyLoading,
} from '@givelify/givelify-ui';
import { styled } from '@mui/material';
import { useIntegration } from 'pages/integrations/hooks';
import { useIntegrationSettingsUpdate } from 'pages/integrations/hooks';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { AppState } from 'store';

const SettingsContainer = styled('div')(() => ({
    '& .description': {
        fontWeight: 700,
    },
}));

export const PCIntegrationSettings: React.FCC = () => {
    const { t } = useTranslation();
    const { userDoneeId } = useSelector((state: AppState) => ({
        userDoneeId: state.User.user.doneeId,
    }));
    const text = React.useMemo(
        () => ({
            header: t('pages.integrations.settings.planningCenter.header'),
            label: t('pages.integrations.settings.planningCenter.label'),
        }),
        [t],
    );
    const { integration, isFetching: isLoading } = useIntegration(userDoneeId, {
        type: 'PLANNING_CENTER',
        doneeId: userDoneeId,
        settings: {
            donationsAutoexport: true,
        },
        credentials: {
            username: '',
            url: '',
        },
    });
    const { mutate: updateIntegrationSettings } =
        useIntegrationSettingsUpdate();
    const [checked, setChecked] = React.useState(false);
    React.useEffect(() => {
        setChecked(integration?.settings.donationsAutoexport);
    }, [integration]);
    const changeAutoExport = React.useCallback(
        (value: boolean) => {
            setChecked(value);
            updateIntegrationSettings({
                donorId: userDoneeId,
                data: {
                    donationsAutoexport: value,
                },
            });
        },
        [updateIntegrationSettings, userDoneeId],
    );

    return (
        <SettingsContainer>
            <GivelifyLabel bold text={text.header} variant="heading5" />
            <GivelifyLabel
                className="description"
                marginBottom={16}
                marginTop={8}
                text={text.label}
                variant="body1"
            />
            {isLoading ? <GivelifyLoading type="circular" /> : null}
            {!isLoading ? (
                <GivelifySwitch
                    checked={checked}
                    onChange={changeAutoExport}
                    variant="thick-labeled"
                />
            ) : null}
        </SettingsContainer>
    );
};
